import { DEFAULT_BLOG_AUTHOR } from "@/lib/blogAuthor";

type Props = {
  /** "sm" for an inline byline row (blog posts, solution pages). "lg" for a page hero (About). */
  size?: "sm" | "lg";
  className?: string;
};

type GuestBylineProps = {
  name: string;
  jobTitle?: string;
  profileUrl: string;
  className?: string;
};

/**
 * The one place Patrick's photo + byline are defined. Reused wherever "By Patrick,
 * Founder" appears so the site shows the same face and the same words every time,
 * instead of a different treatment per page.
 */
export default function FounderByline({ size = "sm", className = "" }: Props) {
  const avatarSize = size === "lg" ? "h-14 w-14" : "h-9 w-9";
  const textSize = size === "lg" ? "text-base" : "text-sm";

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <img
        src={DEFAULT_BLOG_AUTHOR.avatarUrl}
        alt={`${DEFAULT_BLOG_AUTHOR.name}, ${DEFAULT_BLOG_AUTHOR.jobTitle}`}
        className={`${avatarSize} flex-shrink-0 rounded-full border border-green-200/70 object-cover`}
        loading="lazy"
      />
      <p className={`${textSize} text-muted-foreground`}>
        By{" "}
        <a href={DEFAULT_BLOG_AUTHOR.profileUrl} className="text-primary underline underline-offset-2">
          {DEFAULT_BLOG_AUTHOR.name}
        </a>
        , {DEFAULT_BLOG_AUTHOR.jobTitle}
      </p>
    </div>
  );
}

/** Text-only byline for guest contributors (no founder photo). */
export function GuestByline({
  name,
  jobTitle = "Guest writer",
  profileUrl,
  className = "",
}: GuestBylineProps) {
  const external = /^https?:\/\//i.test(profileUrl);
  return (
    <p className={`text-sm text-muted-foreground ${className}`}>
      By{" "}
      <a
        href={profileUrl}
        className="text-primary underline underline-offset-2"
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {name}
      </a>
      , {jobTitle}
    </p>
  );
}
